/**
 * AiPriority.js — AI-Assisted Patch Prioritization Dashboard
 * Shows patches ranked by AI priority score with explanations.
 * Integrates with Executive Dashboard, Patch Catalog, and Deployment pages.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
}

const SEV_COLOR = { critical:'#dc2626', high:'#ea580c', medium:'#d97706', low:'#16a34a' };
const SEV_BG    = { critical:'#fee2e2', high:'#ffedd5', medium:'#fef3c7', low:'#dcfce7' };
const CAT_ICON  = { os:'🪟', browser:'🌐', application:'📦', driver:'🔧', bios:'⚡', firmware:'💾' };

const LABEL_STYLE = {
  'DEPLOY NOW':  { bg:'#fee2e2', color:'#dc2626', icon:'🔴', bar:'#dc2626' },
  'THIS WEEK':   { bg:'#ffedd5', color:'#ea580c', icon:'🟠', bar:'#ea580c' },
  'NEXT CYCLE':  { bg:'#fef3c7', color:'#d97706', icon:'🟡', bar:'#d97706' },
  'ON SCHEDULE': { bg:'#f0fdf4', color:'#16a34a', icon:'🟢', bar:'#16a34a' },
};

function ScoreBar({ score, label }) {
  const style = LABEL_STYLE[label] || LABEL_STYLE['ON SCHEDULE'];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, background:'#f1f5f9', borderRadius:4, height:8, overflow:'hidden', minWidth:60 }}>
        <div style={{ width:`${score}%`, height:'100%', background:style.bar, borderRadius:4,
          transition:'width 0.5s' }} />
      </div>
      <span style={{ fontSize:12, fontWeight:700, color:style.color, minWidth:28 }}>{score}</span>
    </div>
  );
}

export default function AiPriority() {
  const [patches,   setPatches]   = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [refreshing,setRefreshing]= useState(false);
  const [filter,    setFilter]    = useState('all');
  const [sevFilter, setSevFilter] = useState('all');
  const [search,    setSearch]    = useState('');
  const [expandRow, setExpandRow] = useState(null);
  const [msg,       setMsg]       = useState('');
  const [computedAt,setComputedAt]= useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const [pRes, sRes] = await Promise.all([
        api.getAiPriorities(),
        api.getAiPrioritySummary()
      ]);
      setPatches(pRes.data.results || []);
      setSummary(sRes.data);
      setComputedAt(pRes.data.computedAt);
    } catch (e) {
      setMsg('Failed to load AI priorities: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function refresh() {
    setRefreshing(true);
    try {
      await api.refreshAiPriority();
      await load();
      setMsg('✔ AI priorities recalculated');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Refresh failed: ' + e.message); }
    finally { setRefreshing(false); }
  }

  const filtered = patches.filter(p => {
    if (filter !== 'all' && p.label !== filter) return false;
    if (sevFilter !== 'all' && p.severity !== sevFilter) return false;
    if (search && ![p.patchName, p.vendor, p.product].join(' ').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const deployNow = patches.filter(p => p.label === 'DEPLOY NOW').length;
  const thisWeek  = patches.filter(p => p.label === 'THIS WEEK').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🧠 AI Patch Prioritization</div>
          <div className="page-subtitle">
            Patches ranked by AI priority score · Severity + Exploit risk + Age + Impact
            {computedAt && <span style={{ color:'#94a3b8', marginLeft:12, fontSize:11 }}>
              Computed: {fmtDate(computedAt)}
            </span>}
          </div>
        </div>
        <div className="flex-gap">
          <button className="btn btn-ghost" onClick={load} disabled={loading}>↻ Refresh</button>
          <button className="btn btn-primary" onClick={refresh} disabled={refreshing}>
            {refreshing ? '⟳ Recalculating...' : '🧠 Recalculate'}
          </button>
        </div>
      </div>

      {msg && (
        <div className={`alert ${msg.startsWith('✔') ? 'success' : 'error'}`} style={{ marginBottom:16 }}>
          {msg}
        </div>
      )}

      {/* How it works banner */}
      <div style={{ background:'#eff4ff', border:'1px solid #bfdbfe', borderRadius:10,
        padding:'12px 20px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ fontSize:22 }}>🧠</span>
        <div style={{ fontSize:12, color:'#1e2a3b', lineHeight:1.6 }}>
          <strong>How the AI score is calculated:</strong>
          {' '}Severity (35%) · Exploit signals (25%) · Days since release (20%) · Devices unpatched (10%) · Patch category (10%)
          <br/>
          <span style={{ color:'#64748b' }}>
            Score 90-100 = Deploy Now · 70-89 = This Week · 40-69 = Next Cycle · 0-39 = On Schedule
          </span>
        </div>
      </div>

      {/* Summary KPI cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'DEPLOY NOW',  count:summary?.summary?.['DEPLOY NOW']  || 0, ...LABEL_STYLE['DEPLOY NOW'] },
          { label:'THIS WEEK',   count:summary?.summary?.['THIS WEEK']   || 0, ...LABEL_STYLE['THIS WEEK'] },
          { label:'NEXT CYCLE',  count:summary?.summary?.['NEXT CYCLE']  || 0, ...LABEL_STYLE['NEXT CYCLE'] },
          { label:'ON SCHEDULE', count:summary?.summary?.['ON SCHEDULE'] || 0, ...LABEL_STYLE['ON SCHEDULE'] },
        ].map(c => (
          <div key={c.label} onClick={() => setFilter(filter === c.label ? 'all' : c.label)}
            style={{ background: filter===c.label ? c.color : c.bg, color: filter===c.label ? '#fff' : c.color,
              border:`1px solid ${c.bg}`, borderRadius:10, padding:'14px 18px', cursor:'pointer',
              transition:'all 0.15s', boxShadow: filter===c.label ? `0 4px 12px ${c.color}40` : 'none' }}>
            <div style={{ fontSize:20, marginBottom:4 }}>{c.icon}</div>
            <div style={{ fontSize:26, fontWeight:800 }}>{c.count}</div>
            <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase',
              letterSpacing:'0.5px', marginTop:2, opacity:0.85 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <input className="form-input" style={{ maxWidth:240 }}
          placeholder="Search patch, vendor..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display:'flex', gap:6 }}>
          {['all','critical','high','medium','low'].map(s => (
            <button key={s} className={`btn btn-sm ${sevFilter===s?'btn-primary':'btn-outline'}`}
              onClick={() => setSevFilter(s)}
              style={sevFilter===s && s!=='all' ? { background:SEV_COLOR[s], color:'#fff', border:'none' } : {}}>
              {s==='all'?'All Severity':s.charAt(0).toUpperCase()+s.slice(1)}
            </button>
          ))}
        </div>
        <span style={{ marginLeft:'auto', fontSize:12, color:'#94a3b8' }}>
          {filtered.length} patch{filtered.length!==1?'es':''} shown
        </span>
      </div>

      {/* AI Priority Table */}
      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">🧠</div>
            AI Priority Rankings
          </div>
          {(deployNow > 0 || thisWeek > 0) && (
            <span style={{ fontSize:12, fontWeight:600, color:'#dc2626',
              background:'#fee2e2', padding:'4px 12px', borderRadius:20 }}>
              ⚠ {deployNow} require immediate action · {thisWeek} this week
            </span>
          )}
        </div>

        {loading ? <div className="loading">Calculating AI priorities...</div> : (
          <table>
            <thead>
              <tr>
                <th style={{ width:36 }}></th>
                <th>Patch</th>
                <th>Vendor</th>
                <th>Version</th>
                <th>Severity</th>
                <th>Devices Pending</th>
                <th>Released</th>
                <th>AI Score</th>
                <th>Priority</th>
                <th style={{ width:36 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="empty">No patches match your filters.</td></tr>
              )}
              {filtered.map((p, i) => {
                const ls = LABEL_STYLE[p.label] || LABEL_STYLE['ON SCHEDULE'];
                const isExpanded = expandRow === p.patchId;
                return (
                  <React.Fragment key={p.patchId}>
                    <tr style={{
                      background: p.label === 'DEPLOY NOW' ? '#fff8f8' : undefined,
                      borderLeft: p.label === 'DEPLOY NOW' ? '3px solid #dc2626'
                                : p.label === 'THIS WEEK' ? '3px solid #ea580c'
                                : '3px solid transparent'
                    }}>
                      <td style={{ textAlign:'center', fontSize:18 }}>
                        {CAT_ICON[p.category] || '📦'}
                      </td>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13, color:'#1e2a3b' }}>{p.patchName}</div>
                        <div style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>{p.category}</div>
                      </td>
                      <td style={{ fontSize:13, color:'#64748b' }}>{p.vendor}</td>
                      <td style={{ fontSize:12, color:'#64748b' }}>{p.version}</td>
                      <td>
                        <span style={{ fontSize:11, fontWeight:700, background:SEV_BG[p.severity],
                          color:SEV_COLOR[p.severity], padding:'2px 8px', borderRadius:12 }}>
                          {p.severity}
                        </span>
                      </td>
                      <td>
                        {p.devicesPending > 0 ? (
                          <span style={{ fontSize:13, fontWeight:600, color:'#d97706' }}>
                            ⏳ {p.devicesPending} / {p.devicesTotal}
                          </span>
                        ) : (
                          <span style={{ fontSize:12, color:'#16a34a' }}>✔ all patched</span>
                        )}
                      </td>
                      <td style={{ fontSize:12, color:'#94a3b8' }}>
                        {fmtDate(p.releaseDate)}
                        {p.daysUnpatched !== null && p.daysUnpatched > 0 && (
                          <div style={{ fontSize:10, color: p.daysUnpatched > 30 ? '#dc2626' : '#94a3b8' }}>
                            {p.daysUnpatched}d ago
                          </div>
                        )}
                      </td>
                      <td style={{ minWidth:120 }}>
                        <ScoreBar score={p.score} label={p.label} />
                      </td>
                      <td>
                        <span style={{ fontSize:11, fontWeight:700, background:ls.bg,
                          color:ls.color, padding:'3px 10px', borderRadius:12, whiteSpace:'nowrap' }}>
                          {ls.icon} {p.label}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => setExpandRow(isExpanded ? null : p.patchId)}
                          style={{ background:'none', border:'none', cursor:'pointer',
                            fontSize:12, color:'#94a3b8', padding:'2px 4px' }}>
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={10} style={{ background:'#f8fafc', padding:'12px 20px',
                          borderTop:'1px solid #e8ecf4' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                            {/* AI Reason */}
                            <div style={{ gridColumn:'1/-1' }}>
                              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8',
                                textTransform:'uppercase', marginBottom:4 }}>AI Reasoning</div>
                              <div style={{ fontSize:13, color:'#1e2a3b', background:'#fff',
                                padding:'8px 12px', borderRadius:8, border:'1px solid #e8ecf4',
                                borderLeft:`3px solid ${ls.color}` }}>
                                {ls.icon} <strong>{p.label}</strong> (Score: {p.score}/100) — {p.reason}
                              </div>
                            </div>
                            {/* Score breakdown */}
                            {p.scores && (
                              <div style={{ gridColumn:'1/-1' }}>
                                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8',
                                  textTransform:'uppercase', marginBottom:8 }}>Score Breakdown</div>
                                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                                  {[
                                    { label:'Severity (35%)',     val:p.scores.severity, max:10 },
                                    { label:'Exploit Risk (25%)', val:p.scores.exploit,  max:10 },
                                    { label:'Days Old (20%)',     val:p.scores.days,     max:10 },
                                    { label:'Devices (10%)',      val:p.scores.devices,  max:10 },
                                    { label:'Category (10%)',     val:p.scores.category, max:10 },
                                  ].map(s => (
                                    <div key={s.label} style={{ flex:1, minWidth:120 }}>
                                      <div style={{ display:'flex', justifyContent:'space-between',
                                        fontSize:11, color:'#64748b', marginBottom:3 }}>
                                        <span>{s.label}</span>
                                        <span style={{ fontWeight:700, color:'#1e2a3b' }}>{s.val}/10</span>
                                      </div>
                                      <div style={{ background:'#e8ecf4', borderRadius:4,
                                        height:6, overflow:'hidden' }}>
                                        <div style={{ width:`${s.val/s.max*100}%`, height:'100%',
                                          background:ls.color, borderRadius:4 }} />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
